import React from 'react'
import './AdvancedStyle.css'
import { InputField, ToggleSwitch } from '@enamelai/design-system'
import MessagingAutoReplyEditor from '../../Messaging/MessagingAutoReplyEditor.component'
import { type EmailAdvancedSettings } from '../useEmail'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'

type AdvancedTabProps = {
  data: EmailAdvancedSettings
  onChange: <K extends keyof EmailAdvancedSettings>(
    key: K,
    value: EmailAdvancedSettings[K],
  ) => void
}

const AUTO_REPLY_MIN = new Date(2020, 0, 1)
const AUTO_REPLY_MAX = new Date(2035, 11, 31)

const pad = (value: number): string => String(value).padStart(2, '0')

const toDate = (iso: string): Date | null => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatDateValue = (iso: string): string => {
  const date = toDate(iso)
  if (!date) return ''
  return `${pad(date.getMonth() + 1)} / ${pad(date.getDate())} / ${date.getFullYear()}`
}

const formatTimeValue = (iso: string): string => {
  const date = toDate(iso)
  if (!date) return ''
  const hours24 = date.getHours()
  const period = hours24 >= 12 ? 'PM' : 'AM'
  let hours12 = hours24 % 12
  if (hours12 === 0) hours12 = 12
  return `${pad(hours12)} : ${pad(date.getMinutes())} ${period}`
}

const isAutoReplyDateDisabled = (date: Date): boolean => {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  return day < AUTO_REPLY_MIN || day > AUTO_REPLY_MAX
}

const mergeDate = (iso: string, date: Date): string => {
  const next = toDate(iso) ?? new Date(date)
  next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
  return next.toISOString()
}

const mergeTime = (iso: string, time: Date): string => {
  const next = toDate(iso) ?? new Date(time)
  next.setHours(time.getHours(), time.getMinutes(), 0, 0)
  return next.toISOString()
}

const clearTime = (iso: string): string => {
  const date = toDate(iso)
  if (!date) return ''
  date.setHours(0, 0, 0, 0)
  return date.toISOString()
}

type DateTimeFieldProps = {
  label: string
  iso: string
  disabled: boolean
  onChange: (next: string) => void
}

const DateTimeField = ({
  label,
  iso,
  disabled,
  onChange,
}: DateTimeFieldProps): React.ReactElement => (
  <div className="scp-email-advanced-field">
    <InputField
      variant="date"
      label={label}
      fullWidth
      value={formatDateValue(iso)}
      disabled={disabled}
      isDateDisabled={isAutoReplyDateDisabled}
      onDateChange={(date) => onChange(mergeDate(iso, date))}
      onClear={() => onChange('')}
    />
    <InputField
      variant="time"
      label=""
      fullWidth
      value={formatTimeValue(iso)}
      disabled={disabled}
      onTimeChange={(time) => onChange(mergeTime(iso, time))}
      onClear={() => onChange(clearTime(iso))}
    />
  </div>
)

const AdvancedTab = ({
  data,
  onChange,
}: AdvancedTabProps): React.ReactElement => (
  <div className="scp-email-advanced">
    <div className="scp-email-advanced-section-header">
      <span className="scp-email-advanced-section-title">
        Automatic replies
        <span
          className="ds-section-card__icon-button"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Send an automatic reply when you are away."
        >
          <InfoIcon aria-hidden="true" />
        </span>
      </span>
      <ToggleSwitch
        id="email-automatic-replies"
        aria-label="Automatic replies"
        checked={data.automatic_replies_enabled}
        onChange={(checked) => onChange('automatic_replies_enabled', checked)}
      />
    </div>

    <div className="scp-email-advanced-field-row">
      <DateTimeField
        label="From"
        iso={data.from_datetime}
        disabled={!data.automatic_replies_enabled}
        onChange={(next) => onChange('from_datetime', next)}
      />
      <DateTimeField
        label="To"
        iso={data.to_datetime}
        disabled={!data.automatic_replies_enabled}
        onChange={(next) => onChange('to_datetime', next)}
      />
    </div>

    <div className="scp-email-advanced-subject-field">
      <InputField
        variant="text"
        label="Subject"
        fullWidth
        value={data.subject}
        disabled={!data.automatic_replies_enabled}
        onChange={(event) => onChange('subject', event.target.value)}
      />
    </div>

    <div className="scp-email-advanced-message-editor">
      <MessagingAutoReplyEditor
        value={data.message}
        disabled={!data.automatic_replies_enabled}
        editorHeight="200px"
        onChange={(next) => onChange('message', next)}
      />
    </div>
  </div>
)

export default AdvancedTab
